import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import EntityChip from '../../components/assistant/EntityChip';
import { collectEntityRefIds, hasNoEntityRefs } from '../../lib/assistant/entityRefIds';

import type {
  GetTranscriptEntitiesQuery,
  GetTranscriptEntitiesQueryVariables,
} from './__generated__/useTranscriptEntities.gql';
import type { EntityChipTarget } from '../../components/assistant/EntityChip';
import type { IConversationMessage } from '../../hooks/useAssistantWebSocket';
import type { EntityRefType } from '../../lib/assistant/remarkAssistantMarkup';

/**
 * `@skip` rather than an empty-array call: each of these resolvers rejects an
 * empty `ids` list, and a transcript usually references only some of the five
 * record types.
 */
const GET_TRANSCRIPT_ENTITIES = gql`
  query GetTranscriptEntities(
    $merchantIds: [ID!]!
    $accountIds: [ID!]!
    $transactionIds: [ID!]!
    $billIds: [ID!]!
    $recurringTransactionIds: [ID!]!
    $skipMerchants: Boolean!
    $skipAccounts: Boolean!
    $skipTransactions: Boolean!
    $skipBills: Boolean!
    $skipRecurringTransactions: Boolean!
  ) {
    getMerchants(ids: $merchantIds) @skip(if: $skipMerchants) {
      ...EntityChipMerchant
    }
    getFinancialAccounts(ids: $accountIds) @skip(if: $skipAccounts) {
      ...EntityChipAccount
    }
    getTransactions(ids: $transactionIds) @skip(if: $skipTransactions) {
      ...EntityChipTransaction
    }
    getBills(ids: $billIds) @skip(if: $skipBills) {
      ...EntityChipBill
    }
    getRecurringTransactions(ids: $recurringTransactionIds) @skip(if: $skipRecurringTransactions) {
      ...EntityChipRecurringTransaction
    }
  }
  ${EntityChip.MerchantFragment}
  ${EntityChip.AccountFragment}
  ${EntityChip.TransactionFragment}
  ${EntityChip.BillFragment}
  ${EntityChip.RecurringTransactionFragment}
`;

/** Resolved rows keyed by record type, then by id. */
export type TranscriptEntities = Record<EntityRefType, Record<string, EntityChipTarget>>;

const EMPTY: TranscriptEntities = {
  merchant: {},
  financial_account: {},
  transaction: {},
  bill: {},
  recurring_transaction: {},
};

const byId = (rows: readonly { id: string }[] | null | undefined): Record<string, EntityChipTarget> =>
  Object.fromEntries((rows ?? []).map((row) => [row.id, row as EntityChipTarget]));

/**
 * Resolve every entity the transcript's replies reference inline, keyed for the
 * chips that render them.
 *
 * Hoisted to the pane for the same reason the alert-card lookup is: the view
 * fetches nothing, so its stories render from fixtures with no Apollo client.
 * One query for the whole transcript, not one per chip.
 *
 * An id that resolves to nothing — hallucinated by the model, or scoped away —
 * is simply absent, and the chip renders its unresolved label. That is the only
 * handling a wrong id gets: resolution runs through GraphQL, which is
 * viewer-scoped, so a foreign id cannot come back.
 *
 * @param messages - the transcript, scanned for the ids its reference tokens name.
 * @returns the resolved rows keyed by record type then id; unresolved ids are absent.
 */
export function useTranscriptEntities(messages: IConversationMessage[]): TranscriptEntities {
  const refIds = React.useMemo(() => collectEntityRefIds(messages), [messages]);
  const skipAll = hasNoEntityRefs(refIds);

  const { data, previousData } = useQuery<GetTranscriptEntitiesQuery, GetTranscriptEntitiesQueryVariables>(
    GET_TRANSCRIPT_ENTITIES,
    {
      variables: {
        merchantIds: refIds.merchant,
        accountIds: refIds.financial_account,
        transactionIds: refIds.transaction,
        billIds: refIds.bill,
        recurringTransactionIds: refIds.recurring_transaction,
        skipMerchants: refIds.merchant.length === 0,
        skipAccounts: refIds.financial_account.length === 0,
        skipTransactions: refIds.transaction.length === 0,
        skipBills: refIds.bill.length === 0,
        skipRecurringTransactions: refIds.recurring_transaction.length === 0,
      },
      skip: skipAll,
    }
  );

  return React.useMemo(() => {
    if (skipAll) {
      return EMPTY;
    }
    // `previousData` covers the window where a mutation evicted these fields and
    // the refetch is in flight — without it every chip in the transcript would
    // flash its unresolved label.
    const resolved = data ?? previousData;
    return {
      merchant: byId(resolved?.getMerchants),
      financial_account: byId(resolved?.getFinancialAccounts),
      transaction: byId(resolved?.getTransactions),
      bill: byId(resolved?.getBills),
      recurring_transaction: byId(resolved?.getRecurringTransactions),
    };
  }, [data, previousData, skipAll]);
}
